/**
 * Persistent honest-status strip for the Pool detail view.
 *
 * Rendered above the active tab's content on EVERY pool tab, so the
 * one-sentence derived story (`Pool.status_summary`) is in view no
 * matter where the operator is reading — Spec, Events, Logs, … .
 * This is the surface that resolves the incident's contradiction:
 * the header no longer says a calm "Launching" while the Events tab
 * screams errors; both defer to the same server-derived sentence.
 *
 * Renders nothing when `attention_level === "ok"` — a healthy pool
 * doesn't need a banner, and an always-on strip would train
 * operators to ignore it.
 */
import { RefreshCw, ShieldAlert } from "lucide-react";

import type { Pool } from "@/api/types";

export function PoolStatusBanner({ pool }: { pool: Pool }) {
  if (pool.attention_level === "ok") return null;
  const danger = pool.attention_level === "action_required";
  return (
    <div
      className={
        danger
          ? "status-banner status-banner--danger"
          : "status-banner status-banner--warn"
      }
      role="status"
    >
      {danger ? (
        <ShieldAlert size={14} aria-hidden />
      ) : (
        <RefreshCw size={14} aria-hidden />
      )}
      <div className="status-banner__body">
        <strong>
          {danger
            ? "Needs attention — operator action required"
            : "Self-healing — no action needed (keep an eye on progress)"}
        </strong>
        <p>{pool.status_summary}</p>
      </div>
    </div>
  );
}
